import {
  useState,
  useEffect,
  useCallback,
  useContext,
  type ReactElement
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import LabelWithInfo from '../../../components/LabelWithInfo/LabelWithInfo';
import { Checkbox } from '../../../components/ui/checkbox';
import CustomInput from '../../../components/CustomInput/CustomInput';
import RichTextEditor from '../../../components/RichTextEditor/RichTextEditor';
import ColorInput from './ColorInput';
import FontSelect from './FontSelect';
import ChatbotStylingFormValues from '../../../types/ChatbotStylingFormValues';
import SettingsService from '../../../services/SettingsService/SettingsService';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import useAbortController from '../../../hooks/useAbortController';
import NotificationSnackbar from '../../../components/NotificationSnackbar/NotificationSnackbar';
import FileUploadZone from '../../../components/FileUploadZone/FileUploadZone';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { CHATBOT_FONT_PREVIEW_STYLESHEET_URL } from '../../../constants/chatbotFonts';

export default function ChatbotStylingTab(): ReactElement {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState('');
  const [shouldDeleteImage, setShouldDeleteImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { currentChatbot } = useContext(ChatbotContext);
  const { getAbortSignal } = useAbortController();

  const validationSchema = Yup.object().shape({
    botName: Yup.string()
      .required('Chatbot header is required')
      .max(25, 'Chatbot header must be 25 characters or less'),
    ctaLabel: Yup.string().max(
      30,
      'Button label must be 30 characters or less'
    ),
    ctaUrl: Yup.string()
      .url('Enter a valid URL (including https://)')
      .when('ctaLabel', {
        is: (label: string) => !!label && label.trim().length > 0,
        then: (schema) =>
          schema.required('A link is required when a button label is set')
      }),
    minimisedText: Yup.string().max(
      25,
      'Minimised label must be 25 characters or less'
    ),
    disclaimerText: Yup.string().max(
      500,
      'Disclaimer must be 500 characters or less'
    )
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<ChatbotStylingFormValues>({
    resolver: yupResolver(validationSchema)
  });

  const rgbaToHex = (rgba: string): string => {
    const regex = /rgba?\((\d+),\s*(\d+),\s*(\d+)/;
    const match = regex.exec(rgba);
    if (!match) return rgba;

    const r = Number.parseInt(match[1], 10);
    const g = Number.parseInt(match[2], 10);
    const b = Number.parseInt(match[3], 10);

    const toHex = (n: number): string => {
      const hex = n.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const hexToRgba = (hex: string, alpha: number = 1): string => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const fetchSettings = useCallback(async () => {
    if (!currentChatbot) return;
    setErrorMessage('');
    setSuccessMessage('');
    const signal = getAbortSignal();

    try {
      const styling = await SettingsService.getStylingSettings(
        currentChatbot.id,
        signal
      );

      setValue('botName', styling.botName);
      setValue('headerBackground', rgbaToHex(styling.headerBackground));
      setValue('headerText', rgbaToHex(styling.headerText));
      setValue(
        'responseCardBackground',
        rgbaToHex(styling.responseCardBackground)
      );
      setValue('responseCardText', rgbaToHex(styling.responseCardText));
      setValue(
        'userMessageBackground',
        rgbaToHex(styling.userMessageBackground)
      );
      setValue(
        'userMessageTextMaximiseButtonText',
        rgbaToHex(styling.userMessageTextMaximiseButtonText)
      );
      setValue('mainBackground', rgbaToHex(styling.mainBackground));
      setValue('mainText', rgbaToHex(styling.mainText));
      setValue(
        'chatbotMessageBackground',
        rgbaToHex(styling.chatbotMessageBackground)
      );
      setValue('chatbotMessageText', rgbaToHex(styling.chatbotMessageText));
      setValue('inputBackground', rgbaToHex(styling.inputBackground));
      setValue('inputText', rgbaToHex(styling.inputText));
      setValue('ctaLabel', styling.ctaLabel ?? '');
      setValue('ctaUrl', styling.ctaUrl ?? '');
      setValue('minimisedText', styling.minimisedText ?? '');
      setValue('disclaimerText', styling.disclaimerText ?? '');
      setValue('ctaBackground', rgbaToHex(styling.ctaBackground));
      setValue('ctaText', rgbaToHex(styling.ctaText));
      setValue('disclaimerColour', rgbaToHex(styling.disclaimerColour));
      setValue('hideChatbotImage', styling.hideChatbotImage ?? false);
      setValue('titleFontFamily', styling.titleFontFamily);
      setValue('bodyFontFamily', styling.bodyFontFamily);

      if (styling.customBotHeadUrl) {
        setFilePreviewUrl(styling.customBotHeadUrl);
        setSelectedFile(null);
      } else {
        setFilePreviewUrl('');
        setSelectedFile(null);
      }
      setShouldDeleteImage(false);
    } catch (err: any) {
      if (err.name !== 'CanceledError') {
        setErrorMessage('Failed to load settings');
        setShowError(true);
      }
    } finally {
      setIsLoadingData(false);
    }
  }, [currentChatbot, getAbortSignal, setValue]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleClearImage = (): void => {
    setSelectedFile(null);
    setFilePreviewUrl('');
    setShouldDeleteImage(true);
  };

  const handleFileSelect = (file: File | null): void => {
    setSelectedFile(file);
    if (file) {
      setShouldDeleteImage(false);
    }
  };

  const onSubmit = async (data: ChatbotStylingFormValues): Promise<void> => {
    setErrorMessage('');
    setSuccessMessage('');
    if (!currentChatbot) return;

    try {
      setIsLoading(true);

      const stylingSettings = {
        botName: data.botName,
        headerBackground: hexToRgba(data.headerBackground),
        headerText: hexToRgba(data.headerText),
        responseCardBackground: hexToRgba(data.responseCardBackground),
        responseCardText: hexToRgba(data.responseCardText),
        userMessageBackground: hexToRgba(data.userMessageBackground),
        userMessageTextMaximiseButtonText: hexToRgba(
          data.userMessageTextMaximiseButtonText
        ),
        mainBackground: hexToRgba(data.mainBackground),
        mainText: hexToRgba(data.mainText),
        chatbotMessageBackground: hexToRgba(data.chatbotMessageBackground),
        chatbotMessageText: hexToRgba(data.chatbotMessageText),
        inputBackground: hexToRgba(data.inputBackground),
        inputText: hexToRgba(data.inputText),
        ctaLabel: data.ctaLabel?.trim() ?? '',
        ctaUrl: data.ctaUrl?.trim() ?? '',
        minimisedText: data.minimisedText?.trim() ?? '',
        disclaimerText: data.disclaimerText?.trim() ?? '',
        ctaBackground: hexToRgba(data.ctaBackground),
        ctaText: hexToRgba(data.ctaText),
        disclaimerColour: hexToRgba(data.disclaimerColour),
        hideChatbotImage: data.hideChatbotImage ?? false,
        titleFontFamily: data.titleFontFamily,
        bodyFontFamily: data.bodyFontFamily,
        deleteImage: shouldDeleteImage
      };

      const response = await SettingsService.updateStylingSettings(
        currentChatbot.id,
        stylingSettings,
        selectedFile
      );

      if (selectedFile && response.customBotHeadUrl) {
        setSelectedFile(null);
        setFilePreviewUrl(response.customBotHeadUrl);
      }

      setShouldDeleteImage(false);
      setSuccessMessage('Settings updated successfully');
      setShowSuccess(true);
    } catch (err: any) {
      if (err.name !== 'CanceledError') {
        setErrorMessage(err.message || 'Something went wrong');
        setShowError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) return <LoadingSpinner size={200} />;

  return (
    <div className='flex flex-1 flex-col'>
      <Helmet>
        <title>Japeto - Chatbot Styling</title>
        <link rel='stylesheet' href={CHATBOT_FONT_PREVIEW_STYLESHEET_URL} />
      </Helmet>

      <form onSubmit={handleSubmit(onSubmit)} className='w-[56rem] max-w-full'>
        <div className='space-y-8'>
          <div className='flex flex-col lg:flex-row gap-8 items-start'>
            <div className='w-full lg:w-1/2 space-y-4'>
              <LabelWithInfo
                className='text-base'
                info='Basic details shown on the chat interface.'
              >
                General
              </LabelWithInfo>
              <div className='space-y-2'>
                <LabelWithInfo
                  htmlFor='chatbot-header'
                  info='The title shown at the top of the chat window.'
                >
                  Chatbot header
                </LabelWithInfo>
                <Controller
                  name='botName'
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      id='chatbot-header'
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder='Enter chatbot header'
                      maxLength={25}
                    />
                  )}
                />
                {errors.botName && (
                  <p className='text-destructive text-sm'>
                    {errors.botName.message}
                  </p>
                )}
              </div>

              <div className='space-y-2'>
                <LabelWithInfo
                  htmlFor='minimised-text'
                  info='Text shown on the minimised chat interface button.'
                >
                  Minimised label
                </LabelWithInfo>
                <Controller
                  name='minimisedText'
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      id='minimised-text'
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder='e.g. Get debt help'
                      maxLength={25}
                    />
                  )}
                />
                {errors.minimisedText && (
                  <p className='text-destructive text-sm'>
                    {errors.minimisedText.message}
                  </p>
                )}
              </div>

              <div className='space-y-2'>
                <LabelWithInfo info='Avatar shown in the chat header.'>
                  Chatbot image
                </LabelWithInfo>
                <FileUploadZone
                  filePreviewUrl={filePreviewUrl}
                  setFile={handleFileSelect}
                  setPreviewUrl={setFilePreviewUrl}
                  maxSizeBytes={200 * 1024}
                  onClear={handleClearImage}
                />
                <Controller
                  name='hideChatbotImage'
                  control={control}
                  render={({ field }) => (
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='hide-chatbot-image'
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                      <Label
                        htmlFor='hide-chatbot-image'
                        className='font-normal cursor-pointer'
                      >
                        Hide chatbot image
                      </Label>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className='w-full lg:w-1/2 space-y-6'>
              <div className='space-y-4'>
                <LabelWithInfo
                  className='text-base'
                  info='An optional button shown in the chat, e.g. to reach a human.'
                >
                  Call to action
                </LabelWithInfo>
                <div className='space-y-2'>
                  <LabelWithInfo
                    htmlFor='cta-label'
                    info='Text shown on the CTA button. Leave blank to hide it.'
                  >
                    CTA button label
                  </LabelWithInfo>
                  <Controller
                    name='ctaLabel'
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        id='cta-label'
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder='e.g. Contact a person'
                        maxLength={30}
                      />
                    )}
                  />
                  {errors.ctaLabel && (
                    <p className='text-destructive text-sm'>
                      {errors.ctaLabel.message}
                    </p>
                  )}
                </div>
                <div className='space-y-2'>
                  <LabelWithInfo
                    htmlFor='cta-url'
                    info='The URL opened when the CTA button is clicked.'
                  >
                    CTA button link
                  </LabelWithInfo>
                  <Controller
                    name='ctaUrl'
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        id='cta-url'
                        type='url'
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder='https://example.com/contact'
                      />
                    )}
                  />
                  {errors.ctaUrl && (
                    <p className='text-destructive text-sm'>
                      {errors.ctaUrl.message}
                    </p>
                  )}
                </div>
              </div>

              <div className='space-y-2'>
                <LabelWithInfo
                  className='text-base'
                  info='Small print shown beneath the chat, e.g. a privacy notice.'
                >
                  Disclaimer
                </LabelWithInfo>
                <Controller
                  name='disclaimerText'
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      textContent={field.value}
                      onChange={field.onChange}
                      outputFormat='markdown'
                      showToolbar
                      showVariableButton={false}
                      showPreviewButton={false}
                      className='styling-disclaimer-editor'
                      placeholder='e.g. Always sense check advice. Read our privacy policy.'
                    />
                  )}
                />
                {errors.disclaimerText && (
                  <p className='text-destructive text-sm'>
                    {errors.disclaimerText.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='space-y-4'>
            <LabelWithInfo
              className='text-base'
              info='Choose the fonts of the chat interface to match your brand.'
            >
              Fonts
            </LabelWithInfo>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4'>
              <FontSelect
                id='title-font'
                label='Title font'
                name='titleFontFamily'
                control={control}
                tooltip='Font of the chat window header and of headings in messages.'
              />

              <FontSelect
                id='body-font'
                label='Body font'
                name='bodyFontFamily'
                control={control}
                tooltip='Font of all other text: messages, follow up options, buttons and inputs.'
              />
            </div>
          </div>

          <div className='space-y-4'>
            <LabelWithInfo
              className='text-base'
              info='Customise the colours of the chat interface to match your brand.'
            >
              Colours
            </LabelWithInfo>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4'>
              <ColorInput
                id='header-background'
                label='Header background'
                name='headerBackground'
                control={control}
                errors={errors}
                tooltip='Background colour of the chat window header.'
              />

              <ColorInput
                id='header-text'
                label='Header text'
                name='headerText'
                control={control}
                errors={errors}
                tooltip='Colour of the text in the header.'
              />

              <ColorInput
                id='response-card-bg'
                label='Follow up options background'
                name='responseCardBackground'
                control={control}
                errors={errors}
                tooltip='Background colour of follow up option buttons.'
              />

              <ColorInput
                id='response-card-text'
                label='Follow up options text'
                name='responseCardText'
                control={control}
                errors={errors}
                tooltip='Text colour of follow up option buttons.'
              />

              <ColorInput
                id='user-message-bg'
                label='User message background'
                name='userMessageBackground'
                control={control}
                errors={errors}
                tooltip='Background colour of messages sent by the user.'
              />

              <ColorInput
                id='user-message-text'
                label='User message text'
                name='userMessageTextMaximiseButtonText'
                control={control}
                errors={errors}
                tooltip='Text colour of the user’s messages.'
              />

              <ColorInput
                id='main-background'
                label='Main background'
                name='mainBackground'
                control={control}
                errors={errors}
                tooltip='Background colour of the conversation area.'
              />

              <ColorInput
                id='main-text'
                label='Character count text'
                name='mainText'
                control={control}
                errors={errors}
                tooltip='Colour of the character count shown under the message input.'
              />

              <ColorInput
                id='chatbot-message-bg'
                label='Chatbot message background'
                name='chatbotMessageBackground'
                control={control}
                errors={errors}
                tooltip='Background colour of messages from the chatbot.'
              />

              <ColorInput
                id='chatbot-message-text'
                label='Chatbot message text'
                name='chatbotMessageText'
                control={control}
                errors={errors}
                tooltip='Text colour of the chatbot’s messages.'
              />

              <ColorInput
                id='input-background'
                label='Message input background'
                name='inputBackground'
                control={control}
                errors={errors}
                tooltip='Background colour of the message input field.'
              />

              <ColorInput
                id='input-text'
                label='Message input text'
                name='inputText'
                control={control}
                errors={errors}
                tooltip='Colour of the text typed into the message input field.'
              />

              <ColorInput
                id='cta-background'
                label='CTA button background'
                name='ctaBackground'
                control={control}
                errors={errors}
                tooltip='Background colour of the CTA button.'
              />

              <ColorInput
                id='cta-text'
                label='CTA button text'
                name='ctaText'
                control={control}
                errors={errors}
                tooltip='Text colour of the CTA button.'
              />

              <ColorInput
                id='disclaimer-colour'
                label='Disclaimer text colour'
                name='disclaimerColour'
                control={control}
                errors={errors}
                tooltip='Colour of the disclaimer text.'
              />
            </div>
          </div>

          <div className='flex justify-end pt-2'>
            <Button type='submit' disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </div>
      </form>
      <NotificationSnackbar
        open={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
        severity='error'
      />
      <NotificationSnackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={successMessage}
        severity='success'
      />
    </div>
  );
}
